import React, { useState, useEffect } from "react";

import { fetchImage } from "../../api/photo-upload";
import {
  escapedNewLineToLineBreakTag,
  isString
} from "../../utils/string-utils";
import themePropTypes from "../theme-prop-types";

const VpcpInfo = ({ components: { Tooltip }, label, tooltip }) => (
  <div className='provider-detail-group-web-sdk'>
    <div className='provider-detail-item-web-sdk vpc-info-wrapper'>
      <Tooltip message={tooltip}>
        <div className='vpc-info-wrapper-ico' />
      </Tooltip>
      <p className='text-web-sdk vpcp-label' data-testid='provider-vpcp-label'>
        {label}
      </p>
    </div>
  </div>
);

const Provider = ({
  provider,
  components,
  onSelect,
  onViewProfile,
  screenName,
  settings
}) => {
  const {
    bio,
    name,
    role,
    gender,
    languages,
    provider_specialties,
    provider_credentials,
    slot_availability,
    special_training,
    website_url,
    vpcpLabel,
    vpcpTooltip,
    earliestAvailability,
    unavailabilities
  } = provider.detail_data;
  const { accepts_video, profile_image, can_prescribe } = provider.cell_data;
  const { provider_id, provider_name, provider_can_prescribe } =
    provider.url_request_data;
  const { Button } = components;
  const buttonTitle =
    screenName === "VpcpEnhancedSchedulingProviderList"
      ? settings.translations.select_provider
      : settings.translations.request_appointment;

  const [avatar, setAvatar] = useState();

  useEffect(() => {
    if (profile_image) {
      fetchImage(profile_image).then(source => source && setAvatar(source));
    }
  }, []);

  let availability = slot_availability || "";
  const isAvailabilityString = isString(availability);

  if (isAvailabilityString && availability.startsWith("(")) {
    const index = availability.indexOf(" ");
    availability = availability.slice(index + 1);
  }

  const escapedDatesMarkup = unavailabilities
    ? escapedNewLineToLineBreakTag(unavailabilities)
    : null;

  const { call, video } = settings.translations;

  return (
    /* eslint-disable react/jsx-one-expression-per-line */
    <div
      className='provider-wrapper-web-sdk'
      data-testid={`provider-wrapper-${provider_id}`}
    >
      <div className='provider-web-sdk'>
        <div className='provider-header-web-sdk'>
          <div className='provider-profile-picture-wrapper-web-sdk'>
            {avatar ? (
              /* eslint-disable jsx-a11y/img-redundant-alt */
              <img
                className='provider-profile-picture-web-sdk'
                src={avatar}
                data-testid='provider-avatar'
                alt='profile picture'
              />
            ) : (
              <div className='provider-profile-picture-web-sdk-ico' />
            )}
          </div>
          <div className='provider-indicators-web-sdk'>
            {accepts_video && (
              <div
                className='provider-indicator-web-sdk provider-indicator-web-sdk-video-ico'
                data-testid='provider-video-icon'
                aria-label={video}
                role='img'
              />
            )}
            <div
              className='provider-indicator-web-sdk provider-indicator-web-sdk-phone-ico'
              data-testid='provider-phone-icon'
              aria-label={call}
              role='img'
            />
            {can_prescribe && (
              <div
                className='provider-indicator-web-sdk provider-indicator-web-sdk-pill-ico'
                data-testid='provider-pill-icon'
              />
            )}
          </div>
          <div className='provider-name-web-sdk' data-testid='provider-name'>
            {name}
          </div>
          <div className='provider-profile-link-wrapper-web-sdk'>
            <button
              type='button'
              className='provider-profile-link-web-sdk'
              onClick={e => {
                e.preventDefault();
                onViewProfile(name);
              }}
              data-testid='provider-view-profile-button'
              aria-label={`${name}, ${settings.translations.view_profile}`}
            >
              {settings.translations.view_profile}{" "}
              <span aria-hidden='true'>&rsaquo;</span>
            </button>
          </div>
          <div className='provider-title-web-sdk' data-testid='provider-role'>
            {role}
          </div>
          <div className='provider-details-web-sdk'>
            <div className='provider-detail-group-web-sdk'>
              <div className='provider-detail-item-web-sdk'>
                <div className='provider-detail-item-title-web-sdk'>
                  {settings.translations.gender}
                </div>
                <div
                  className='provider-detail-item-value-web-sdk'
                  data-testid='provider-gender'
                >
                  {gender}
                </div>
              </div>
              <div className='provider-detail-item-web-sdk'>
                <div className='provider-detail-item-title-web-sdk'>
                  {settings.translations.languages}
                </div>
                <div
                  className='provider-detail-item-value-web-sdk'
                  data-testid='provider-languages'
                >
                  {languages}
                </div>
              </div>
            </div>
            <div className='provider-detail-group-web-sdk'>
              <div className='provider-detail-item-web-sdk'>
                <div className='provider-detail-item-title-web-sdk'>
                  {settings.translations.specialties}
                </div>
                <div
                  className='provider-detail-item-value-web-sdk'
                  data-testid='provider-specialties'
                >
                  {provider_specialties}
                </div>
              </div>
            </div>
            {vpcpLabel && (
              <VpcpInfo
                components={components}
                label={vpcpLabel}
                tooltip={vpcpTooltip}
              />
            )}
          </div>
        </div>
        <div
          className='provider-availability-web-sdk'
          data-testid='provider-availability'
        >
          {availability && (
            <>
              <div className='provider-availability-title-web-sdk'>
                {settings.translations.provider_is_available}
              </div>
              <div
                className='provider-availability-content-web-sdk'
                data-testid='provider-availability-content'
              >
                {isAvailabilityString
                  ? availability
                      .split(", ")
                      .map(item => <div key={item}>{item}</div>)
                  : availability}
              </div>
            </>
          )}
          {earliestAvailability && (
            <>
              <div className='provider-availability-title-web-sdk'>
                {settings.translations.earliest_availability}
              </div>
              <div
                className='provider-availability-content-web-sdk'
                data-testid='provider-availability-content'
              >
                {earliestAvailability}
              </div>
            </>
          )}
          {unavailabilities && (
            <>
              <div className='provider-availability-title-web-sdk'>
                {settings.translations.not_available}
              </div>
              <div
                className='provider-availability-content-web-sdk'
                data-testid='provider-unaviability-content'
              >
                {escapedDatesMarkup}
              </div>
            </>
          )}
          <Button
            className='secondary bold websdk-block'
            label={buttonTitle}
            ariaLabel={`${name}, ${buttonTitle}`}
            name='provider-appointment'
            onClick={() =>
              onSelect({
                provider_id,
                provider_name,
                provider_can_prescribe,
                cell_data: {
                  accepts_video
                },
                detail_data: {
                  bio,
                  gender,
                  languages,
                  name,
                  provider_credentials,
                  provider_specialties,
                  role,
                  special_training,
                  website_url
                }
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

Provider.propTypes = themePropTypes.providerControl;
VpcpInfo.propTypes = themePropTypes.vpcpInfo;

export default Provider;
